// components/sticker/ai-sticker.tsx - AI task sticker following sticker pattern

import {
    arrow,
    autoUpdate,
    flip,
    FloatingArrow,
    FloatingPortal,
    offset,
    safePolygon,
    shift,
    useFloating,
    useHover,
    useInteractions
} from '@floating-ui/react';
import { AITaskResponseResultData } from 'api/collaborateApi';
import { ArrowLeft, ArrowRight, Plus, Sparkles, Trash2 } from 'lucide-react';
import { animate, AnimatePresence, motion, useAnimationControls, useMotionValue, useTransform } from 'motion/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from 'ui';
import { cn } from 'utils/cn';
import { useFloatingUIManager } from '../../hooks/use-floating-manager';
import { useAppDispatch, useAppSelector } from '../../store';
import { getCurrentHoverStickerId, setCurrentHoverStickerId } from '../../store/tasksSlice';
import { ConvertedAiTask } from '../../types/ai-task';
import { getNearestNode } from '../../utils/node';
import { AiTask, useAiTaskActions } from '../tasks/ai-task';

interface AiStickerProps {
    containerRef: React.RefObject<HTMLDivElement | null>;
    aiTask: Partial<AITaskResponseResultData>;
    task: ConvertedAiTask; // The converted task object
    positioned?: boolean;
    inClusterPanel?: boolean;
    animationDelay?: number;
    shouldAnimate?: boolean;
    onAnimationComplete?: () => void;
}

export const AiSticker = ({
    aiTask,
    task,
    positioned = true,
    inClusterPanel = false,
    animationDelay = 0,
    shouldAnimate = false,
    onAnimationComplete
}: AiStickerProps) => {
    const { t } = useTranslation();
    const [validElementFound, setValidElementFound] = useState(false);
    const [hasFixedAncestor, setHasFixedAncestor] = useState(false);
    const targetElementRef = useRef<Element | null>(null);
    const lastPositionRef = useRef<{ top: number; left: number } | null>(null);
    const animationControls = useAnimationControls();
    const [hasInitiallyAnimated, setHasInitiallyAnimated] = useState(false);
    const [isCurrentlyAnimating, setIsCurrentlyAnimating] = useState(false);
    const hoverArrowRef = useRef<SVGSVGElement | null>(null);

    // Get portal root for FloatingPortal
    const getPortalRoot = useCallback(() => {
        const shadowRoot = document.getElementById('root-atarim')?.shadowRoot;
        if (shadowRoot) {
            const appWrapper = shadowRoot.getElementById('app-wrapper');
            return appWrapper || null;
        }
        return null;
    }, []);

    // Use the floating UI manager hook - only for hover, not task panels
    const { shouldAllowHover } = useFloatingUIManager();

    // Hover tooltip floating UI
    const dispatch = useAppDispatch();
    const currentHoverStickerId = useAppSelector(getCurrentHoverStickerId);
    const [isHoverOpen, setIsHoverOpen] = useState(false);
    const {
        refs: hoverRefs,
        floatingStyles: hoverFloatingStyles,
        context: hoverContext
    } = useFloating({
        open: isHoverOpen && currentHoverStickerId === `AI:${String(aiTask.id ?? '')}`,
        onOpenChange: (open) => {
            if (open) {
                dispatch(setCurrentHoverStickerId(`AI:${String(aiTask.id ?? '')}`));
                setIsHoverOpen(true);
            } else {
                if (currentHoverStickerId === `AI:${String(aiTask.id ?? '')}`) {
                    dispatch(setCurrentHoverStickerId(null));
                }
                setIsHoverOpen(false);
            }
        },
        middleware: [
            offset(8),
            flip({
                padding: 16,
                fallbackPlacements: inClusterPanel ?
                    ['bottom', 'top-start', 'top-end', 'bottom-start', 'bottom-end'] :
                    ['left', 'top', 'bottom', 'top-start', 'top-end', 'bottom-start', 'bottom-end'],
                fallbackStrategy: 'bestFit',
            }),
            shift({
                padding: 16,
            }),
            arrow({ element: hoverArrowRef, padding: 16 }),
        ],
        placement: inClusterPanel ? 'bottom' : 'right',
        whileElementsMounted: autoUpdate,
    });

    const hover = useHover(hoverContext, {
        delay: { open: 500, close: 0 },
        restMs: 100,
        handleClose: safePolygon({
            blockPointerEvents: true,
        }),
    });

    const { getReferenceProps: getHoverReferenceProps, getFloatingProps: getHoverFloatingProps } = useInteractions([
        hover,
    ]);
    // Accept/Dismiss actions for swipe gestures on hover panel
    const { acceptTask, dismissTask } = useAiTaskActions(aiTask);
    const panelX = useMotionValue(0);
    const rotate = useTransform(panelX, [-160, 0, 160], [-8, 0, 8]);
    const cardOpacity = useMotionValue(1);
    const cardScale = useMotionValue(1);
    const [isDragging, setIsDragging] = useState(false);
    // Get the current localStorage value directly
    const getHasSeenSwipeHint = () => {
        try {
            return localStorage.getItem('ai-sticker-swipe-hint-seen') === 'true';
        } catch (_e) {
            return false;
        }
    };

    // We don't need the state anymore since we're using the function directly

    // Drag indicator opacity and scale calculations
    const leftIndicatorOpacity = useTransform(panelX, [-160, -80, 0], [1, 0.75, 0.5]);
    const rightIndicatorOpacity = useTransform(panelX, [0, 80, 160], [0.5, 0.75, 1]);
    const leftIndicatorScale = useTransform(panelX, [-160, -80, 0], [1.5, 1.1, 1]);
    const rightIndicatorScale = useTransform(panelX, [0, 80, 160], [1, 1.1, 1.5]);

    // Border states for trigger feedback
    const leftIndicatorBorder = useTransform(panelX, [-80, -79], [1, 0]);
    const rightIndicatorBorder = useTransform(panelX, [79, 80], [0, 1]);

    // Box shadow transforms for border feedback
    const leftIndicatorBoxShadow = useTransform(leftIndicatorBorder, (value) =>
        value > 0.5 ? '0 0 0 4px rgba(39, 45, 60, 0.2), 0 4px 6px -1px rgba(0, 0, 0, 0.1)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
    );
    const rightIndicatorBoxShadow = useTransform(rightIndicatorBorder, (value) =>
        value > 0.5 ? '0 0 0 4px rgba(62, 214, 150, 0.2), 0 4px 6px -1px rgba(0, 0, 0, 0.1)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
    );

    // Motion values for indicator entrance animations
    const leftIndicatorEntrance = useMotionValue(0);
    const rightIndicatorEntrance = useMotionValue(0);

    // No need to sync state since we're reading localStorage directly
    // Animate indicators in for first-time users when hover opens
    useEffect(() => {
        if (isHoverOpen && !getHasSeenSwipeHint()) {
            // Animate indicators in from center for first-time users
            animate(leftIndicatorEntrance, 1, { duration: 0.4, ease: 'easeOut', delay: 0.2 });
            animate(rightIndicatorEntrance, 1, { duration: 0.4, ease: 'easeOut', delay: 0.3 });
        } else if (!isHoverOpen && !getHasSeenSwipeHint()) {
            // Reset when hover closes
            leftIndicatorEntrance.set(0);
            rightIndicatorEntrance.set(0);
        }
    }, [isHoverOpen, leftIndicatorEntrance, rightIndicatorEntrance]);

    // Transform entrance values to x positions
    const leftIndicatorEntranceX = useTransform(leftIndicatorEntrance, [0, 1], [0, -80]);
    const rightIndicatorEntranceX = useTransform(rightIndicatorEntrance, [0, 1], [0, 80]);

    // Combined scale values that work for both scenarios
    const leftIndicatorFinalScale = useTransform(
        [leftIndicatorEntrance, leftIndicatorScale],
        (values: number[]) => {
            const [entrance, dragScale] = values;
            if (getHasSeenSwipeHint()) {
                // For experienced users: combine entrance scale with drag scale
                return entrance * dragScale;
            } else {
                // For first-time users: use entrance scale (0.8 to 1.0)
                return 0.8 + (entrance * 0.2);
            }
        }
    );
    const rightIndicatorFinalScale = useTransform(
        [rightIndicatorEntrance, rightIndicatorScale],
        (values: number[]) => {
            const [entrance, dragScale] = values;
            if (getHasSeenSwipeHint()) {
                // For experienced users: combine entrance scale with drag scale
                return entrance * dragScale;
            } else {
                // For first-time users: use entrance scale (0.8 to 1.0)
                return 0.8 + (entrance * 0.2);
            }
        }
    );

    // Combined opacity values that work for both scenarios
    const leftIndicatorFinalOpacity = useTransform(
        [leftIndicatorEntrance, leftIndicatorOpacity],
        (values: number[]) => {
            const [entrance, dragOpacity] = values;
            if (getHasSeenSwipeHint()) {
                // For experienced users: combine entrance opacity with drag opacity
                return entrance * dragOpacity;
            } else {
                // For first-time users: use entrance opacity (0 to dragOpacity)
                return entrance * dragOpacity;
            }
        }
    );
    const rightIndicatorFinalOpacity = useTransform(
        [rightIndicatorEntrance, rightIndicatorOpacity],
        (values: number[]) => {
            const [entrance, dragOpacity] = values;
            if (getHasSeenSwipeHint()) {
                // For experienced users: combine entrance opacity with drag opacity
                return entrance * dragOpacity;
            } else {
                // For first-time users: use entrance opacity (0 to dragOpacity)
                return entrance * dragOpacity;
            }
        }
    );

    // Prevent page overflow while dragging (no horizontal scrollbars on host page)
    useEffect(() => {
        if (!isDragging) return;
        const prevDoc = document.documentElement.style.overflowX;
        const prevBody = document.body.style.overflowX;
        document.documentElement.style.overflowX = 'hidden';
        document.body.style.overflowX = 'hidden';
        return () => {
            document.documentElement.style.overflowX = prevDoc;
            document.body.style.overflowX = prevBody;
        };
    }, [isDragging]);

    // Drag thresholds and handlers
    const DRAG_THRESHOLD = 80; // px - matches the indicator positions
    const handleDragStart = () => {
        setIsDragging(true);
        // Mark the hint as seen when user starts dragging
        if (!getHasSeenSwipeHint()) {
            try {
                localStorage.setItem('ai-sticker-swipe-hint-seen', 'true');
            } catch (_e) {
                // Optionally log the error, or ignore
            }
        }

        // Animate indicators in when dragging starts
        if (getHasSeenSwipeHint()) {
            animate(leftIndicatorEntrance, 1, { duration: 0.3, ease: 'easeOut' });
            animate(rightIndicatorEntrance, 1, { duration: 0.3, ease: 'easeOut' });
        }
    };

    const handleDragEnd = async (_: unknown, info: { offset: { x: number, y: number } }) => {
        const { offset } = info;
        const id = String(aiTask.id || '');
        if (!id) return;

        if (offset.x > DRAG_THRESHOLD) {
            // Accept to the right: gently fade out (no large slide)
            await Promise.all([
                animate(cardOpacity, 0, { duration: 0.2, ease: 'easeOut' }),
                animate(cardScale, 0.95, { duration: 0.2, ease: 'easeOut' }),
            ]);
            await acceptTask(id);
            setIsHoverOpen(false);
            panelX.set(0);
            cardOpacity.set(1);
            cardScale.set(1);
            setIsDragging(false);
        } else if (offset.x < -DRAG_THRESHOLD) {
            // Dismiss to the left: gently fade out (no large slide)
            await Promise.all([
                animate(cardOpacity, 0, { duration: 0.2, ease: 'easeOut' }),
                animate(cardScale, 0.95, { duration: 0.2, ease: 'easeOut' }),
            ]);
            await dismissTask(id);
            setIsHoverOpen(false);
            panelX.set(0);
            cardOpacity.set(1);
            cardScale.set(1);
            setIsDragging(false);
        } else {
            // Not enough swipe — snap back
            await animate(panelX, 0, { type: 'spring', stiffness: 500, damping: 35 });
            await Promise.all([
                animate(cardOpacity, 1, { duration: 0.15 }),
                animate(cardScale, 1, { duration: 0.15 }),
            ]);
            setIsDragging(false);

            // Animate indicators out when dragging ends
            if (getHasSeenSwipeHint()) {
                animate(leftIndicatorEntrance, 0, { duration: 0.2, ease: 'easeIn' });
                animate(rightIndicatorEntrance, 0, { duration: 0.2, ease: 'easeIn' });
            }
        }
    };


    // Ensure floating UI reference is always set for hover only
    const stickerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (stickerRef.current && inClusterPanel) {
            if (typeof hoverRefs.setReference === 'function') {
                hoverRefs.setReference(stickerRef.current);
            }
        }
    }, [task.task_id, hoverRefs, inClusterPanel]);

    // If any task panel is open, we still allow hovering AI stickers, so no global close here

    // Sync local open state with global hover id changes
    useEffect(() => {
        const id = `AI:${String(aiTask.id ?? '')}`;
        if (currentHoverStickerId !== id && isHoverOpen) {
            setIsHoverOpen(false);
        }
    }, [currentHoverStickerId, aiTask.id, isHoverOpen]);

    // Check if element has fixed positioning ancestor
    const hasFixedPosition = (element: Element | null): boolean => {
        if (!element) return false;

        let currentElement: Element | null = element;

        while (currentElement && currentElement !== document.body && currentElement !== document.documentElement) {
            const style = window.getComputedStyle(currentElement);
            if (style.position === 'fixed') {
                return true;
            }
            currentElement = currentElement.parentElement;
        }

        return false;
    };

    // Check if element is valid and visible
    const isElementValid = (targetElement: Element | null) => {
        if (!targetElement) return false;

        const rect = targetElement.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) return false;

        const style = window.getComputedStyle(targetElement);
        if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
            return false;
        }

        const isAtOrigin = rect.top === 0 && rect.left === 0 && rect.right === 0 && rect.bottom === 0;
        if (isAtOrigin) return false;

        return true;
    };

    // Calculate position based on target element
    const calculatePosition = useCallback(() => {
        if (inClusterPanel) {
            return { top: 0, left: 0 };
        }

        if (!targetElementRef.current) return { top: 0, left: 0 };

        const rect = targetElementRef.current.getBoundingClientRect();

        if (hasFixedAncestor) {
            // For elements with fixed ancestors, use viewport coordinates
            return {
                top: rect.top + (rect.height / 2),
                left: rect.left + (rect.width / 2)
            };
        } else {
            // For regular elements, use document coordinates
            const scrollY = window.scrollY || document.documentElement.scrollTop;
            const scrollX = window.scrollX || document.documentElement.scrollLeft;

            return {
                top: rect.top + scrollY + (rect.height / 2),
                left: rect.left + scrollX + (rect.width / 2)
            };
        }
    }, [hasFixedAncestor, inClusterPanel]);

    // Clamp horizontal position to viewport
    const clampToViewport = useCallback((pos: { top: number; left: number }) => {
        const H_MARGIN = 16;
        const viewportWidth = window.innerWidth;
        const scrollX = window.scrollX || document.documentElement.scrollLeft;
        // Measure current sticker width when available; fallback to 32
        const stickerWidth = (stickerRef.current?.offsetWidth ?? 32);

        const minLeft = hasFixedAncestor ? H_MARGIN : scrollX + H_MARGIN;
        const maxLeft = (hasFixedAncestor ? 0 : scrollX) + viewportWidth - stickerWidth - H_MARGIN;
        const clampedLeft = Math.max(minLeft, Math.min(pos.left, maxLeft));
        return { top: pos.top, left: clampedLeft };
    }, [hasFixedAncestor]);

    // Throttled position update
    const updateStickerPosition = useCallback(() => {
        if (!validElementFound || isCurrentlyAnimating || inClusterPanel) return;

        const newPosition = clampToViewport(calculatePosition());

        if (
            !lastPositionRef.current ||
            Math.abs(newPosition.top - lastPositionRef.current.top) > 2 ||
            Math.abs(newPosition.left - lastPositionRef.current.left) > 2
        ) {
            animationControls.set({
                position: hasFixedAncestor ? 'fixed' : 'absolute',
                top: newPosition.top,
                left: newPosition.left,
            });

            lastPositionRef.current = newPosition;
        }
    }, [animationControls, validElementFound, hasFixedAncestor, calculatePosition, isCurrentlyAnimating, inClusterPanel, clampToViewport]);

    // Find target element using path
    useEffect(() => {
        const findElement = async () => {
            if (aiTask.element?.path) {
                try {
                    const targetElement = await getNearestNode(aiTask.element.path);
                    targetElementRef.current = targetElement;

                    const validElementFound = isElementValid(targetElement);
                    const elementHasFixedAncestor = hasFixedPosition(targetElement);

                    setValidElementFound(validElementFound);
                    setHasFixedAncestor(elementHasFixedAncestor);
                } catch (error) {
                    console.warn('Could not find element for AI task:', aiTask.id, error);
                    setValidElementFound(false);
                }
            } else {
                // If no path provided, use fallback position
                setValidElementFound(true);
                setHasFixedAncestor(false);
            }
        };

        findElement();
    }, [aiTask.element?.path, aiTask.id]);

    // Add event listeners for scroll and resize - with throttling
    useEffect(() => {
        if (!validElementFound || !aiTask.element?.path) return;

        let timeoutId: NodeJS.Timeout;
        const handleScrollOrResize = () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                if (!isCurrentlyAnimating) {
                    requestAnimationFrame(updateStickerPosition);
                }
            }, 16);
        };

        window.addEventListener('scroll', handleScrollOrResize, { passive: true });
        window.addEventListener('resize', handleScrollOrResize, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScrollOrResize);
            window.removeEventListener('resize', handleScrollOrResize);
            clearTimeout(timeoutId);
        };
    }, [validElementFound, aiTask.element?.path, updateStickerPosition, isCurrentlyAnimating]);

    // Initial positioning with animation control
    useEffect(() => {
        if (validElementFound && !hasInitiallyAnimated) {
            if (inClusterPanel) {
                // In cluster panel - handle staggered animation
                if (shouldAnimate) {
                    animationControls.set({
                        scale: 0,
                        position: 'relative',
                        opacity: 0,
                    });

                    const timer = setTimeout(() => {
                        animationControls.start({
                            scale: 1,
                            opacity: 1,
                            transition: {
                                type: "spring",
                                stiffness: 400,
                                damping: 20,
                                duration: 0.25
                            }
                        }).then(() => {
                            setIsCurrentlyAnimating(false);
                            setHasInitiallyAnimated(true);
                            onAnimationComplete?.();
                        });
                    }, animationDelay);

                    return () => clearTimeout(timer);
                } else {
                    // No animation needed
                    animationControls.set({
                        scale: 1,
                        position: 'relative',
                        opacity: 1,
                    });
                    setHasInitiallyAnimated(true);
                    setIsCurrentlyAnimating(false);
                    onAnimationComplete?.();
                }
                return;
            }

            setIsCurrentlyAnimating(true);
            const initialPos = clampToViewport(calculatePosition());
            lastPositionRef.current = initialPos;

            // Initial scaling animation for AI stickers
            animationControls.set({
                position: hasFixedAncestor ? 'fixed' : 'absolute',
                top: initialPos.top,
                left: initialPos.left,
                scale: 0,
            });

            animationControls.start({
                scale: 1,
                transition: {
                    type: "spring",
                    stiffness: 400,
                    damping: 20,
                    duration: 0.25,
                    delay: 0.15 // Slight delay to stagger with regular stickers
                }
            }).then(() => {
                setIsCurrentlyAnimating(false);
                setHasInitiallyAnimated(true);
            });
        }
    }, [
        validElementFound,
        hasFixedAncestor,
        positioned,
        animationControls,
        calculatePosition,
        clampToViewport,
        hasInitiallyAnimated,
        inClusterPanel,
        shouldAnimate,
        animationDelay,
        onAnimationComplete
    ]);

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (isCurrentlyAnimating) return;
        e.stopPropagation();
        // AI tasks only show hover tooltips, no click interaction needed
    };



    const AiStickerContent = () => {
        return (
            <motion.div
                style={{ boxShadow: '0px 0px 20px rgba(109, 93, 243, 0.5)' }}
                className="bg-tertiary relative flex h-8 w-8 items-center justify-center overflow-hidden rounded-[15%_50%_50%_50%] p-1 outline outline-2 outline-white"
            >
                <span className='absolute bg-[#B07DF6] rounded-xl -bottom-2 h-6 w-8 z-1 blur' />
                <span className='absolute bg-[#D5BCF8] rounded-xl -bottom-2.5 h-4 w-6 z-1 blur-sm z-10' />
                <span className="relative z-10 text-xs font-normal text-white">
                    <Sparkles size={16} aria-label={t('ai_sticker.aria.ai_generated', 'AI Task Generated')} />
                </span>
            </motion.div>
        );
    };

    // Render sticker
    const renderSticker = () => (
        <motion.div
            initial={{ scale: inClusterPanel ? 1 : 0 }}
            animate={animationControls}
            style={inClusterPanel ? {
                position: 'relative',
                zIndex: 10, // No special z-index for task panels since AI tasks don't have them
                cursor: 'pointer',
            } : {
                position: hasFixedAncestor ? 'fixed' : 'absolute',
                zIndex: 10, // No special z-index for task panels since AI tasks don't have them
                cursor: 'pointer',
            }}
            className="group/sticker"
            ref={(el) => {
                if (!inClusterPanel) {
                    hoverRefs.setReference(el);
                } else {
                    stickerRef.current = el;
                }
            }}
        >
            <div
                className={`pointer-events-auto cursor-pointer group-hover/sticker:opacity-80 ${inClusterPanel ? 'hover:scale-105 transition-transform' : ''}`}
                onClick={handleClick}
                id={`ai-task-sticker-${aiTask.id}`}
                {...getHoverReferenceProps()}
            >
                <AiStickerContent />
            </div>
        </motion.div>
    );

    // Render hover tooltip
    const renderHoverTooltip = () => {
        if (!isHoverOpen || !shouldAllowHover) return null;

        const tooltip = (
            <div
                ref={hoverRefs.setFloating}
                style={{
                    ...hoverFloatingStyles,
                    zIndex: 999999,
                    overflow: 'visible',
                }}
                {...getHoverFloatingProps()}
                className="pointer-events-auto"
            >
                {/* Left drag indicator (dismiss) - fixed position */}
                {isDragging && (
                    <motion.div
                        style={{
                            opacity: leftIndicatorFinalOpacity,
                            scale: leftIndicatorFinalScale,
                            x: getHasSeenSwipeHint() ? leftIndicatorEntranceX : -80,
                            y: '-50%',
                            boxShadow: leftIndicatorBoxShadow
                        }}
                        className="absolute left-0 top-1/2 flex items-center justify-center w-12 h-12 bg-secondary rounded-full pointer-events-none z-50"
                    >
                        <Trash2 size={20} className="text-white" />
                    </motion.div>
                )}

                {/* Right drag indicator (accept) - fixed position */}
                {isDragging && (
                    <motion.div
                        style={{
                            opacity: rightIndicatorFinalOpacity,
                            scale: rightIndicatorFinalScale,
                            x: getHasSeenSwipeHint() ? rightIndicatorEntranceX : 80,
                            y: '-50%',
                            boxShadow: rightIndicatorBoxShadow
                        }}
                        className="absolute right-0 top-1/2 flex items-center justify-center w-12 h-12 bg-primary rounded-full pointer-events-none z-50"
                    >
                        <Plus size={20} className="text-secondary" />
                    </motion.div>
                )}

                {/* First-time swipe hint */}
                {!getHasSeenSwipeHint() && (
                    <>
                        {/* White overlay for better readability */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.2, duration: 0.3 }}
                            className="absolute flex items-center justify-center left-1/2 -bottom-10 -translate-x-1/2 w-full h-8 bg-background/90 backdrop-blur-sm rounded-xl pointer-events-none z-30 shadow-lg border border-border"
                        >
                            <motion.div
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.2, duration: 0.3 }}
                                className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-full pointer-events-none z-40 w-full"
                            >
                                <ArrowLeft size={16} className="text-gray-700 dark:text-textcolor" />
                                <span className="text-sm font-medium text-gray-700 text-center flex-1 dark:text-textcolor">Swipe to hide or keep</span>
                                <ArrowRight size={16} className="text-gray-700 dark:text-textcolor" />
                            </motion.div>
                        </motion.div>
                    </>
                )}

                <motion.div
                    style={{ x: panelX, rotate, opacity: cardOpacity, scale: cardScale, willChange: 'transform, opacity' }}
                    className={cn(
                        "relative bg-background min-w-0 w-80 max-w-96 p-2.5 rounded-2xl border border-border shadow-md",
                        isDragging && 'cursor-grabbing'
                    )}
                    drag="x"
                    dragElastic={0.2}
                    dragConstraints={{ left: -160, right: 160 }}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    whileDrag={{ scale: 1.02 }}
                >
                    {!isDragging && (
                        <FloatingArrow
                            ref={hoverArrowRef}
                            context={hoverContext}
                            stroke="#e2e2e2"
                            strokeWidth={1}
                            className="fill-background"
                        />
                    )}
                    <AnimatePresence>
                        {!getHasSeenSwipeHint() && (
                            <div className='absolute h-full w-full bg-background/90 backdrop-blur-sm rounded-xl pointer-events-none z-30 shadow-lg border border-border left-0 top-0 flex items-center justify-center'>
                                {/* Swipe demonstration with bordered div */}
                                <div className="relative w-full h-full flex items-center justify-center">
                                    {/* Finger icon that swipes across the div */}
                                    <motion.div
                                        className="absolute left-1/2 -translate-x-1/2 z-40"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ delay: 0.1, duration: 0.1 }}
                                    >
                                        <motion.div
                                            animate={{
                                                rotate: [0, 15, 0, -15, 0],
                                                x: ['-50%', 'calc(-20% )', '-50%', 'calc(-70%)', '-50%']
                                            }}
                                            transition={{
                                                duration: 2.5,
                                                ease: "easeInOut",
                                                repeat: Infinity,
                                                repeatType: "loop"
                                            }}
                                            className='absolute z-50 '>
                                            <svg width="62" height="62" viewBox="0 0 62 62" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                {/* Arrow (top) - black by default, white in dark mode */}
                                                <path
                                                    d="M52.6388 12.3665L52.6388 12.3592L52.595 6.03518C52.5914 5.42977 52.099 4.94471 51.4936 4.94836C50.8919 4.95201 50.4068 5.44071 50.4068 6.04248L50.4068 6.04977L50.436 10.5284C46.2674 5.6413 40.3154 2.59965 33.6157 1.95412C25.9423 1.21742 17.9516 3.72659 11.6386 8.83612L11.6641 4.96295L11.6641 4.95565C11.6641 4.35389 11.179 3.86518 10.5773 3.86153C9.97185 3.85789 9.4795 4.34295 9.47585 4.94836L9.43209 11.3964L9.43209 11.4037C9.43209 11.9689 9.86244 12.4394 10.4241 12.4941L17.503 13.1506C18.1048 13.2053 18.6373 12.764 18.692 12.1622C18.7503 11.5605 18.3054 11.028 17.7036 10.9733L13.0171 10.5393C18.8889 5.79083 26.2997 3.45671 33.4079 4.13871C39.3562 4.7113 44.6554 7.33718 48.4337 11.5605L44.7283 11.9033C44.1594 11.958 43.7363 12.4321 43.7363 12.9901C43.7363 13.0229 43.7363 13.0594 43.74 13.0922C43.7947 13.694 44.3308 14.1353 44.9289 14.0806L51.6431 13.4569C52.2048 13.4059 52.6351 12.9318 52.6351 12.3665L52.6388 12.3665Z"
                                                    fill="black"
                                                    className="dark:fill-white"
                                                />
                                                {/* Hand (middle) - always white */}
                                                <path d="M32.5073 57.2839L43.3099 46.4667L43.5615 46.2296C44.8453 44.9459 45.0495 43.6913 44.9912 42.8634C44.9182 41.8313 44.4113 40.8174 43.5178 39.9275C43.2041 39.6139 42.8431 39.3841 42.4528 39.2346C42.5586 39.0778 42.6534 38.9173 42.7336 38.7495C43.4521 37.2579 43.1421 35.4599 41.9568 34.2782C41.4353 33.7567 40.7934 33.4029 40.0968 33.2352C40.6475 31.8347 40.3485 30.1643 39.2069 29.0228C38.7729 28.5888 38.2551 28.2715 37.6934 28.0782L43.0984 22.6733C43.8241 21.9475 44.2216 20.9847 44.2216 19.9526L44.2216 19.9161C44.2107 18.8767 43.7986 17.8993 43.0619 17.1589C41.5301 15.6272 39.0574 15.6126 37.5475 17.1225L25.5633 29.1067C26.5006 26.8492 26.8835 25.2445 26.7449 24.1905C26.5371 22.5639 25.6144 21.2473 24.2139 20.5762C23.0104 20 21.6391 20.0255 20.6361 20.6419C20.2276 20.8935 19.7754 21.2254 19.5238 21.7761C16.8286 27.6442 15.326 29.9783 13.7322 32.451C13.1706 33.3263 12.5871 34.2272 11.9342 35.314C11.0845 36.729 10.3842 37.8925 10.3587 37.9362C9.32659 39.7598 8.91083 41.4921 8.91083 43.1078C8.91083 48.0349 12.7694 51.868 14.6878 53.7718L16.5733 55.6573C18.9439 58.0279 21.7813 59.545 24.5567 59.928C27.5619 60.3401 30.3847 59.4028 32.5036 57.2839L32.5073 57.2839Z" fill="white" />
                                                {/* Outline (bottom) - always black */}
                                                <path d="M32.5073 57.2839L43.3099 46.4667L43.5615 46.2296C44.8453 44.9459 45.0495 43.6913 44.9912 42.8634C44.9182 41.8313 44.4113 40.8174 43.5178 39.9275C43.2041 39.6139 42.8431 39.3841 42.4528 39.2346C42.5586 39.0778 42.6534 38.9173 42.7336 38.7495C43.4521 37.2579 43.1421 35.4599 41.9568 34.2782C41.4353 33.7567 40.7934 33.4029 40.0968 33.2352C40.6475 31.8347 40.3485 30.1643 39.2069 29.0228C38.7729 28.5888 38.2551 28.2715 37.6934 28.0782L43.0984 22.6733C43.8241 21.9475 44.2216 20.9847 44.2216 19.9526L44.2216 19.9161C44.2107 18.8767 43.7986 17.8993 43.0619 17.1589C41.5301 15.6272 39.0574 15.6126 37.5475 17.1225L25.5633 29.1067C26.5006 26.8492 26.8835 25.2445 26.7449 24.1905C26.5371 22.5639 25.6144 21.2473 24.2139 20.5762C23.0104 20 21.6391 20.0255 20.6361 20.6419C20.2276 20.8935 19.7754 21.2254 19.5238 21.7761C16.8286 27.6442 15.326 29.9783 13.7322 32.451C13.1706 33.3263 12.5871 34.2272 11.9342 35.314C11.0845 36.729 10.3842 37.8925 10.3587 37.9362C9.32659 39.7598 8.91083 41.4921 8.91083 43.1078C8.91083 48.0349 12.7694 51.868 14.6878 53.7718L16.5733 55.6573C18.9439 58.0279 21.7813 59.545 24.5567 59.928C27.5619 60.3401 30.3847 59.4028 32.5036 57.2839L32.5073 57.2839ZM18.1233 54.1073L16.2305 52.2181C13.6702 49.6761 8.90353 44.9422 12.2552 39.0267C12.2917 38.961 12.9044 37.9399 13.8088 36.4409C14.4471 35.3833 15.0196 34.4934 15.5704 33.6327C17.2042 31.0943 18.7469 28.6982 21.4932 22.7134C21.5114 22.6915 21.5698 22.6332 21.7776 22.5055C22.1496 22.2794 22.7295 22.294 23.262 22.5493C23.9805 22.8921 24.4546 23.5923 24.5676 24.4676C24.6625 25.1715 24.316 27.2795 21.0081 33.4759C20.6908 34.0703 20.2969 34.807 21.0628 35.376C21.4968 35.7006 22.1095 35.6532 22.4925 35.2702L39.0902 18.6725C39.7467 18.016 40.8335 18.0342 41.5082 18.7089C41.8401 19.0408 42.0225 19.4748 42.0298 19.938L42.0298 19.9526C42.0298 20.3975 41.8584 20.8169 41.5484 21.1269L33.6926 28.9827L32.6714 30.0039L29.8449 32.8303C29.6298 33.0455 29.524 33.3227 29.524 33.6035C29.524 33.8843 29.6298 34.1652 29.8449 34.3767C30.2716 34.8034 30.9646 34.8034 31.3913 34.3767L35.2353 30.5327C35.5489 30.219 35.972 30.0476 36.4242 30.0513C36.8838 30.0549 37.3214 30.2409 37.6533 30.5728C38.3316 31.2512 38.3462 32.3343 37.6898 32.9908L36.8254 33.8552C36.778 33.9026 36.7379 33.9536 36.7014 34.0083C36.6102 34.0849 36.5191 34.1652 36.4352 34.249L33.8458 36.8385C33.6306 37.0536 33.5248 37.3308 33.5248 37.6116C33.5248 37.8925 33.6306 38.1733 33.8458 38.3848C34.2725 38.8115 34.9654 38.8115 35.3921 38.3848L37.9815 35.7954C38.2988 35.4818 38.7219 35.3103 39.1778 35.314C39.6336 35.314 40.0676 35.5 40.3995 35.8319C40.9284 36.3607 41.0706 37.1521 40.7496 37.8122C40.6731 37.9727 40.5709 38.1186 40.436 38.2499L38.0034 40.6825L37.894 40.7919L37.8466 40.8393C37.6314 41.0545 37.5256 41.3316 37.5256 41.6125C37.5256 41.8933 37.6314 42.1741 37.8466 42.3856C38.2733 42.8123 38.9662 42.8123 39.3929 42.3856L39.4404 42.3382L39.5498 42.2288L39.7686 42.01C40.5709 41.2076 41.4718 40.9888 41.9605 41.4739C43.4193 42.9327 42.6826 44.0049 42.0261 44.6614L41.7745 44.8985L30.9464 55.7375C27.6458 59.0381 22.3685 58.367 18.1087 54.1109L18.1233 54.1073Z" fill="black" />
                                            </svg>
                                        </motion.div>

                                        {/* Bordered div attached to finger tip */}
                                        <motion.div
                                            className='absolute h-24 w-32 border border-border rounded-lg bg-background/50 backdrop-blur-sm left-1/2 z-1 shadow-sm flex flex-col p-2 gap-1'
                                            style={{
                                                top: '-60px',
                                                transformOrigin: 'center bottom',
                                            }}
                                            animate={{
                                                rotate: [0, 15, 0, -15, 0],
                                                x: ['-50%', 'calc(-50% )', '-50%', 'calc(-50%)', '-50%']
                                            }}
                                            transition={{
                                                duration: 2.5,
                                                ease: "easeInOut",
                                                repeat: Infinity,
                                                repeatType: "loop"
                                            }}
                                        >
                                            <div className='flex flex-row gap-0.5'>
                                                <Skeleton className="h-4 w-4 dark:bg-card" />
                                                {/* Skeleton loader for AiTask subtitle/description */}
                                                <div className='flex flex-col gap-1 h-4 w-5/6'>
                                                    <Skeleton className="h-2 w-1/2 dark:bg-card" />
                                                    <Skeleton className="h-2 w-5/6 dark:bg-card" />
                                                </div>
                                            </div>
                                            {/* Skeleton loader for a button or action row */}
                                            <Skeleton className="h-12 w-full  dark:bg-card" />
                                            <div className='flex flex-row gap-0.5'>
                                                <Skeleton className='w-1/2 h-2 dark:bg-card' />
                                                <Skeleton className='w-1/2 h-2 dark:bg-card' />
                                            </div>
                                        </motion.div>


                                        <div
                                            className="absolute top-1/2 -translate-y-1/2 flex items-center justify-center w-8 h-8 bg-primary/80 rounded-full pointer-events-none z-50"
                                            style={{ marginLeft: '75px' }}>
                                            <Plus size={16} className="text-secondary" />
                                        </div>
                                        <div
                                            className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center justify-center w-8 h-8 bg-secondary/80 rounded-full pointer-events-none z-50"
                                            style={{ marginRight: '75px' }}
                                        >
                                            <Trash2 size={16} className="text-white" />
                                        </div>

                                        {/* Right plus icon */}

                                    </motion.div>
                                </div>
                            </div>
                        )
                        }
                    </AnimatePresence >
                    <AiTask item={aiTask} />
                </motion.div >
            </div >

        );

        return (
            <FloatingPortal root={getPortalRoot()}>
                {tooltip}
            </FloatingPortal>
        );
    };

    if (!aiTask || (positioned && !validElementFound && aiTask.element?.path)) {
        return null;
    }

    return (
        <>
            {renderSticker()}
            {renderHoverTooltip()}
        </>
    );
};